/**
 * Sets up a run: suite, arms, trials, cases and judge prompt versions. The checks here repeat the API's
 * (runEvalRequest.ts) so problems show before a run starts; the API checks again.
 *
 * A preset (a saved run's setup) is only the starting state: the parent remounts the form
 * with a new key for each load, and nothing runs until Run is pressed.
 */
import { Play, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../../components/Button';
import InlineErrorDisplay from '../../../components/InlineErrorDisplay';
import { evalsService } from '../services/evalsService';
import type { Effort, EvalModel, EvalSuiteInfo, ListEvalSuitesResponse, RunEvalArm, RunEvalRequest } from '../types/evalTypes';
import { formatDateTime, modelLabel, plural } from '../utils/evalFormat';
import type { RunPreset } from '../utils/rerun';
import { ArmEditor, type ArmDraft } from './ArmEditor';
import { CasePicker } from './CasePicker';
import { JudgeVersionSelect, withJudgeVersion } from './JudgeVersionSelect';

interface NewRunFormProps {
  catalog: ListEvalSuitesResponse;
  running: boolean;
  onStart: (request: RunEvalRequest) => void;
  preset: RunPreset | null;
  onClearPreset: () => void;
}

const DEFAULT_TRIALS = 3;

let nextKey = 1;

function productionArm(suite: EvalSuiteInfo): ArmDraft {
  return { key: `arm-${nextKey++}`, model: suite.productionModel, effort: '', override: null };
}

const toRequestArm = (arm: ArmDraft): RunEvalArm => ({
  model: arm.model,
  ...(arm.effort && { effort: arm.effort }),
  ...(arm.override !== null && { promptOverride: arm.override }),
});

/** The API makes the arm ID from this, so two arms with one setup would get one ID. */
const setupOf = (arm: Pick<ArmDraft, 'model' | 'effort' | 'override'>) => `${arm.model}|${arm.effort}|${arm.override ?? ''}`;

/**
 * A new arm like `base` with a setup no arm has yet: the next effort of the same model, else
 * another model at its default effort. A plain copy when every setup is taken.
 */
function nextArm(base: ArmDraft, arms: ArmDraft[], models: EvalModel[]): ArmDraft {
  const taken = new Set(arms.map(setupOf));
  const efforts: (Effort | '')[] = ['', ...(models.find((model) => model.id === base.model)?.efforts ?? [])];
  const from = efforts.indexOf(base.effort) + 1;
  const candidates: ArmDraft[] = [
    ...[...efforts.slice(from), ...efforts.slice(0, from)].map((effort) => ({ ...base, effort })),
    ...models.filter((model) => !model.judgeOnly).map((model) => ({ ...base, model: model.id, effort: '' as const })),
  ];
  const free = candidates.find((candidate) => !taken.has(setupOf(candidate))) ?? base;
  return { ...free, key: `arm-${nextKey++}` };
}

/** A saved arm as a draft. */
const presetArm = (arm: RunEvalArm): ArmDraft => ({
  key: `arm-${nextKey++}`,
  model: arm.model,
  effort: arm.effort ?? '',
  override: arm.promptOverride ?? null,
});

export const NewRunForm = ({ catalog, running, onStart, preset, onClearPreset }: NewRunFormProps) => {
  const { suites, models, limits } = catalog;
  const presetSuite = preset ? suites.find((candidate) => candidate.id === preset.request.suiteId) : undefined;
  const initialSuite = presetSuite ?? suites[0];

  const [suiteId, setSuiteId] = useState(initialSuite?.id ?? '');
  const suite = suites.find((candidate) => candidate.id === suiteId);

  const [arms, setArms] = useState<ArmDraft[]>(() => {
    if (preset && presetSuite) return preset.request.arms.map(presetArm);
    return initialSuite ? [productionArm(initialSuite)] : [];
  });
  const [trials, setTrials] = useState(preset && presetSuite ? preset.request.trials : DEFAULT_TRIALS);
  const [caseIds, setCaseIds] = useState<Set<string>>(
    () => new Set(preset?.request.caseIds ?? initialSuite?.cases.map((c) => c.id) ?? []),
  );
  // Judge name to version ID; a judge left out uses its current version.
  const [judgeVersions, setJudgeVersions] = useState<Record<string, string>>(() =>
    preset && presetSuite ? (preset.request.judgePromptVersions ?? {}) : {},
  );

  // Production prompts by suite ID, fetched the first time a draft is started.
  const [prompts, setPrompts] = useState<Record<string, string>>({});
  const [promptLoading, setPromptLoading] = useState(false);
  const [promptError, setPromptError] = useState<string | null>(null);

  const selectSuite = (id: string) => {
    const next = suites.find((candidate) => candidate.id === id);
    if (!next) return;
    setSuiteId(id);
    setArms([productionArm(next)]);
    setCaseIds(new Set(next.cases.map((c) => c.id)));
    setJudgeVersions({});
  };

  const updateArm = (key: string, change: Partial<ArmDraft>) => {
    setArms((previous) =>
      previous.map((arm) => (arm.key === key ? { ...arm, ...change } : arm)),
    );
  };

  const toggleDraft = async (key: string, enabled: boolean) => {
    if (!enabled) {
      updateArm(key, { override: null });
      return;
    }
    let prompt: string | undefined = prompts[suiteId];
    if (prompt === undefined) {
      setPromptLoading(true);
      setPromptError(null);
      try {
        ({ prompt } = await evalsService.getPrompt(suiteId));
        const fetched = prompt;
        setPrompts((previous) => ({ ...previous, [suiteId]: fetched }));
      } catch (caught) {
        setPromptError(caught instanceof Error ? caught.message : 'Failed to load the production prompt.');
        return;
      } finally {
        setPromptLoading(false);
      }
    }
    updateArm(key, { override: prompt });
  };

  const addArm = () => {
    setArms((previous) => {
      const last = previous[previous.length - 1];
      const base = last ?? (suite ? productionArm(suite) : null);
      if (!base) return previous;
      return [...previous, nextArm(base, previous, models)];
    });
  };

  const totalTrials = caseIds.size * arms.length * trials;

  const problems = useMemo(() => {
    const found: string[] = [];
    arms.forEach((arm, index) => {
      if (arm.override !== null && arm.override.trim() === '') found.push(`Arm ${index + 1}: the draft prompt is empty.`);
      if (arm.override !== null && arm.override.length > limits.maxPromptOverrideChars) found.push(`Arm ${index + 1}: the draft prompt is too long.`);
    });
    const setups = arms.map(setupOf);
    if (setups.some((setup, index) => setups.indexOf(setup) !== index)) {
      found.push('Two arms have the same model, effort and prompt. Change one, or use more trials to repeat a setup.');
    }
    if (caseIds.size === 0) found.push('Select at least one case.');
    if (totalTrials > limits.maxTrialsPerRun) found.push(`${totalTrials} trials is over the limit of ${limits.maxTrialsPerRun}. Use fewer cases, arms or trials.`);
    suite?.judgePrompts
      .filter((judge) => judge.currentVersionId === null)
      .forEach((judge) => found.push(`The judge "${judge.judgeName}" has no prompt. Create v1 in Judge prompts.`));
    return found;
  }, [arms, caseIds, totalTrials, limits, suite]);

  const start = () => {
    if (!suite || problems.length > 0) return;
    const allCases = caseIds.size === suite.cases.length;
    onStart({
      suiteId: suite.id,
      arms: arms.map(toRequestArm),
      trials,
      // In suite order, so the run lists cases the same way the suite does.
      ...(!allCases && { caseIds: suite.cases.filter((c) => caseIds.has(c.id)).map((c) => c.id) }),
      ...(Object.keys(judgeVersions).length > 0 && { judgePromptVersions: judgeVersions }),
    });
  };

  if (!suite) {
    return <p className="text-sm text-gray-500">No eval suites are available.</p>;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 space-y-4">
      <h2 className="text-lg font-semibold text-gray-900">New run</h2>

      {preset && (
        <div className="text-sm bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-1">
          <div className="flex items-start justify-between gap-3">
            <p className="text-blue-900">
              Loaded the setup of the run from{' '}
              <Link to={`/evals/${preset.runId}`} className="text-blue-600 hover:underline">
                {formatDateTime(preset.startedAt)}
              </Link>
              . Change anything you want, then press Run.
            </p>
            <button type="button" onClick={onClearPreset} disabled={running} className="text-xs text-blue-600 hover:underline shrink-0">
              Clear
            </button>
          </div>
          {preset.notes.length > 0 && (
            <ul className="list-disc list-inside text-amber-800 text-xs space-y-0.5">
              {preset.notes.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Setup on the left, cases on the right; stacked on narrow screens. */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-6 items-start">
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_8rem] gap-3">
            <label className="block">
              <span className="block text-xs font-medium text-gray-600 mb-1">Suite</span>
              <select
                value={suiteId}
                disabled={running}
                onChange={(event) => selectSuite(event.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                {suites.map((candidate) => (
                  <option key={candidate.id} value={candidate.id}>
                    {candidate.id} ({candidate.promptKey})
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-gray-600 mb-1">Trials per case</span>
              <input
                type="number"
                min={1}
                max={limits.maxTrials}
                value={trials}
                disabled={running}
                onChange={(event) => setTrials(Math.min(limits.maxTrials, Math.max(1, Number(event.target.value) || 1)))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </label>
          </div>

          <p className="text-xs text-gray-500">
            Production: {modelLabel(models, suite.productionModel)}, prompt <span className="font-mono">{suite.promptHash}</span>. Judge:{' '}
            {modelLabel(models, catalog.judgeModel)}.
          </p>

          {suite.judgePrompts.map((judge) => (
            <JudgeVersionSelect
              key={judge.judgeName}
              judge={judge}
              value={judgeVersions[judge.judgeName] ?? ''}
              disabled={running}
              onChange={(versionId) => setJudgeVersions((previous) => withJudgeVersion(previous, judge.judgeName, versionId))}
            />
          ))}

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">
                Arms ({arms.length} of {limits.maxArms})
              </span>
              <Button variant="outline" size="sm" icon={<Plus size={14} />} disabled={running || arms.length >= limits.maxArms} onClick={addArm}>
                Add arm
              </Button>
            </div>
            {arms.map((arm) => (
              <ArmEditor
                key={arm.key}
                arm={arm}
                models={models}
                productionModel={suite.productionModel}
                productionPrompt={prompts[suite.id] ?? null}
                promptLoading={promptLoading}
                maxPromptChars={limits.maxPromptOverrideChars}
                canRemove={arms.length > 1}
                disabled={running}
                onChange={(change) => updateArm(arm.key, change)}
                onToggleDraft={(enabled) => void toggleDraft(arm.key, enabled)}
                onRemove={() => setArms((previous) => previous.filter((other) => other.key !== arm.key))}
              />
            ))}
            {promptError && <InlineErrorDisplay message={promptError} onDismiss={() => setPromptError(null)} />}
          </div>
        </div>

        <CasePicker cases={suite.cases} selected={caseIds} disabled={running} onChange={setCaseIds} />
      </div>

      {problems.length > 0 && (
        <ul className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 list-disc list-inside space-y-1">
          {problems.map((problem) => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className={`text-sm ${totalTrials > limits.maxTrialsPerRun ? 'text-red-600' : 'text-gray-600'}`}>
          {plural(caseIds.size, 'case')} × {plural(arms.length, 'arm')} × {plural(trials, 'trial')} = <strong>{totalTrials}</strong> trials (limit{' '}
          {limits.maxTrialsPerRun})
        </span>
        <Button variant="primary" icon={<Play size={14} />} disabled={running || problems.length > 0} onClick={start}>
          Run
        </Button>
      </div>
    </div>
  );
};
